import { AssetGroup, EnvironmentValue } from '@plerionhq/types-asset-group';
import { apiClient } from './api-client';
import { capitalise } from './capitalise';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

type GetAssetGroupResponse = {
  data: AssetGroup[];
};

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'N/A';

export const CONFIDENCE_THRESHOLDS = {
  high: 80,
  medium: 60,
  low: 0,
} as const;

export type EnvironmentSuggestion = {
  confidence: number;
  confidenceLevel: ConfidenceLevel;
  environment: EnvironmentValue;
  environmentId: string;
  method: string;
  reasoning: string;
};

export const useGetEnvironments = (refetchInterval?: number) =>
  useQuery<AssetGroup[], Error>({
    queryKey: ['environments'],
    queryFn: async () => {
      const response = await apiClient.get<GetAssetGroupResponse>(
        `/api/inventory/tenant/asset-groups?dimension=environment`,
      );
      return response.data.data.sort((a, b) => {
        // Sort production first always
        if (
          a.dimension?.value === 'production' &&
          b.dimension?.value !== 'production'
        ) {
          return -1;
        }
        if (
          a.dimension?.value !== 'production' &&
          b.dimension?.value === 'production'
        ) {
          return 1;
        }
        // Sort the rest alphabetically by name, case insensitive
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
    },
    refetchInterval,
  });

/**
 * The display name of an asset's environment, such as "Production", from
 * its `environmentValue`. Reads the tenant's environments, fetched once and
 * shared by every caller; undefined until they load or when none match.
 */
export const useEnvironmentName = (value?: string | null) => {
  const { data: environments } = useGetEnvironments();
  const environment = value
    ? environments?.find(
        ({ dimension }) =>
          dimension?.key === 'environment' && dimension?.value === value,
      )
    : undefined;
  return environment ? capitalise(environment.name) : undefined;
};

export type EnvironmentSuggestionResponse = {
  suggestion: EnvironmentSuggestion | null;
  error: unknown | null;
  integrationId: string;
};

export const useEnvironmentSuggestion = (integrationId: string) =>
  useQuery<EnvironmentSuggestion, Error>({
    queryKey: ['environment-suggestion', integrationId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/core/tenant/integrations/${integrationId}/environment-suggestion`,
      );
      return response.data.data;
    },
    retry: false,
    enabled: !!integrationId,
  });

export const useEnvironmentSuggestions = (integrationIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery<EnvironmentSuggestionResponse[], Error>({
    queryKey: ['environment-suggestions', integrationIds],
    queryFn: async () => {
      const response = await apiClient.post(
        `/api/core/tenant/integrations/suggest-environments`,
        { integrationIds },
      );
      const suggestions = response.data.data;

      suggestions.forEach(
        ({ suggestion, integrationId }: EnvironmentSuggestionResponse) => {
          queryClient.setQueryData(
            ['environment-suggestion', integrationId],
            suggestion ?? undefined,
          );
        },
      );
      return suggestions;
    },
    retry: false,
    enabled: integrationIds.length > 0,
  });
};

export const useUpdateIntegrationEnvironment = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      integrationId,
      environmentId,
    }: {
      integrationId: string;
      environmentId: string;
    }) => {
      const response = await apiClient.put(
        `/api/core/tenant/integrations/${integrationId}`,
        { environmentId },
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (mutation.isSuccess && mutation.variables) {
      queryClient.invalidateQueries({
        queryKey: ['integration', mutation.variables.integrationId],
      });
      queryClient.refetchQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['environments'] });
    }
  }, [mutation.isSuccess, mutation.variables, queryClient]);

  return mutation;
};
